'use client';
// import { getUrlGtm } from '@/utils/gtmUtils';
import GloLayout from 'code-micro/common/GloLayoutA';
import classNames from 'code-micro/utils/classNames';
import { ContentServerProps } from 'code-micro/props/content';
// import getLocal from 'eco-design-reliance/getLocal';
import gtm from 'eco-design-reliance/gtm';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import './index.scss';

/**
 * index-large-card-jp 滚动交互说明
 *
 * 【整体结构】
 * scroll-section（动态高度，撑开滚动距离）
 *   └── hero（sticky + 100vh，滚入视口后固定）
 *         ├── hero-visual（模块触顶 fixed，底边距屏幕底部 = 视口高 - 容器高 时还原）
 *         │     ├── hero-bg
 *         │     └── hero-mask（渐变 + 随滚动叠加白色遮罩）
 *         └── hero-scroll-content（随滚动 translateY 上移）
 *               ├── hero-content（标题 / 描述 / 按钮）
 *               └── products（产品 Swiper，margin-top: auto 贴底）
 *
 * 【滚动阶段】
 * 1. 用户滚入模块：hero sticky 固定在视口，背景图不动
 * 2. 继续滚动：scroll-content 整体上移，mask 随 fixed 阶段逐渐变白（100% = 取消 fixed 时）
 * 3. 产品区差不多全部进入视口后：sticky 结束，整个模块随页面滚走
 *
 * 【高度计算】
 * scrollSection.height = heroHeight(100vh) + maxTranslate
 * maxTranslate = getMaxTranslate(products) —— 产品区进入视口所需的位移
 *
 * 【其它】
 * - 编辑态 / prefers-reduced-motion 下不启用滚动动效
 * - 任意产品卡片标题为 2 行时，产品区 padding-top 从 120px 减为 100px
 */
/**
 * EcoImg 通过 CSS 变量使用 `url('…')`；上传图路径若含单引号、未编码空格等会导致背景失效。
 * 在此规范化后再传入，避免改动 EcoImg 本身。
 */
function toEcoImgBackgroundSrc(src: string | undefined): string | undefined {
    if (src == null || src === '') return src;
    return src.trim().replace(/\\/g, '%5C').replace(/'/g, '%27');
}

/** mask 白色叠加层在滚动结束时的最大透明度（0~1） */
const MASK_MAX_OPACITY = 1;
/** 判定产品区「差不多全部进入视口」时的底部缓冲（px） */
const PRODUCTS_ENTER_BUFFER = 24;

/** 计算 scroll-content 最大上移距离，即 sticky 阶段的滚动行程 */
function getMaxTranslate(productsEl: HTMLElement) {
    const productsTop = productsEl.offsetTop;
    const productsHeight = productsEl.offsetHeight;
    const productsBottom = productsTop + productsHeight;
    const viewportHeight = window.innerHeight;

    // 模块高于视口：滚到顶部贴近视口顶边
    if (productsHeight >= viewportHeight) {
        return Math.max(productsTop - PRODUCTS_ENTER_BUFFER, 0);
    }

    // 模块底部进入视口底边即结束
    return Math.max(productsBottom - viewportHeight + PRODUCTS_ENTER_BUFFER, 0);
}

/** 检测标题是否占 2 行（高度 > 1.5 倍行高） */
function isTitleTwoLines(titleEl: Element) {
    const lineHeight = parseFloat(getComputedStyle(titleEl).lineHeight);
    return lineHeight > 0 && titleEl.getBoundingClientRect().height > lineHeight * 1.5;
}

/** 描述超出 line-clamp 时，点击展开/收起 */
function ItemDesc({ text }: { text: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);

    useLayoutEffect(() => {
        setExpanded(false);
    }, [text]);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || expanded) return;

        const update = () => {
            setCanExpand(el.scrollHeight > el.clientHeight + 1);
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);

        return () => observer.disconnect();
    }, [text, expanded]);

    const isClickable = canExpand || expanded;

    return (
        <div
            ref={ref}
            className={classNames('index-large-card-jp-item-desc', {
                'is-expanded': expanded,
                'is-clickable': isClickable,
            })}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          setExpanded((prev) => !prev);
                      }
                    : undefined
            }
        >
            {text}
        </div>
    );
}

export default function Content({ id, data = {}, initIndex = 0, isEditStatus }: ContentServerProps) {
    // const { locale = 'us' } = getLocal();
    const { title, desc, more_btn_text, more_btn_href, bg_pc, bg_h5, product_title, list = [] } = data;
    const hasHeroBtn = !!more_btn_text && !!more_btn_href;
    const scrollSectionRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const heroVisualRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const productsRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        // 任意卡片标题为 2 行时，缩减产品区上边距，避免模块总高度差异过大
        const productsEl = productsRef.current;
        if (!productsEl) return;

        const update = () => {
            const titles = productsEl.querySelectorAll('.index-large-card-jp-item-title');
            const hasTwoLineTitle = Array.from(titles).some(isTitleTwoLines);
            productsEl.classList.toggle('is-title-two-lines', hasTwoLineTitle);
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(productsEl);
        productsEl.querySelectorAll('.index-large-card-jp-item-title').forEach((el) => observer.observe(el));

        return () => {
            observer.disconnect();
            productsEl.classList.remove('is-title-two-lines');
        };
    }, [list]);

    useEffect(() => {
        // 滚动驱动：背景 sticky 固定 + 内容上移 + mask 渐变
        if (isEditStatus) return;

        const scrollSection = scrollSectionRef.current;
        const hero = heroRef.current;
        const heroVisual = heroVisualRef.current;
        const mask = maskRef.current;
        const content = contentRef.current;
        const products = productsRef.current;
        if (!scrollSection || !hero || !content || !products) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        let rafId = 0;

        const update = () => {
            rafId = 0;
            const heroHeight = hero.offsetHeight;
            const maxTranslate = getMaxTranslate(products);
            const sectionHeight = heroHeight + maxTranslate;

            // 动态撑开 scroll-section，使 sticky 阶段有足够的滚动距离
            scrollSection.style.height = `${sectionHeight}px`;

            const scrolled = -scrollSection.getBoundingClientRect().top;
            const progress = maxTranslate > 0 ? Math.min(Math.max(scrolled / maxTranslate, 0), 1) : 0;

            const sectionRect = scrollSection.getBoundingClientRect();
            const heroVisualHeight = heroVisual?.offsetHeight ?? 0;
            const unfixBottom = heroVisualHeight;
            const maskScrollRange = sectionHeight - heroVisualHeight;

            // 内容位移仍按产品区进入视口进度；mask 按 fixed 阶段进度（0=触顶，1=取消 fixed）
            content.style.transform = `translate3d(0, ${-progress * maxTranslate}px, 0)`;

            let maskProgress = 0;
            if (sectionRect.top <= 0 && maskScrollRange > 0) {
                maskProgress = Math.min(Math.max((sectionHeight - sectionRect.bottom) / maskScrollRange, 0), 1);
            }
            mask?.style.setProperty('--mask-white-opacity', String(maskProgress * MASK_MAX_OPACITY));

            // 模块触顶后 fixed；组件底边距屏幕底部 = 视口高 - hero-visual 高 时还原
            const isVisualFixed = sectionRect.top <= 0 && sectionRect.bottom > unfixBottom;

            if (heroVisual) {
                heroVisual.classList.toggle('is-fixed', isVisualFixed);
                if (isVisualFixed) {
                    const heroRect = hero.getBoundingClientRect();
                    heroVisual.style.left = `${heroRect.left}px`;
                    heroVisual.style.width = `${heroRect.width}px`;
                } else {
                    heroVisual.style.left = '';
                    heroVisual.style.width = '';
                }
            }
        };

        const onScroll = () => {
            if (!rafId) rafId = requestAnimationFrame(update);
        };

        const resizeObserver = new ResizeObserver(() => {
            if (!rafId) rafId = requestAnimationFrame(update);
        });
        resizeObserver.observe(hero);
        resizeObserver.observe(content);
        resizeObserver.observe(products);
        if (heroVisual) resizeObserver.observe(heroVisual);

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update();

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            resizeObserver.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
            scrollSection.style.height = '';
            content.style.transform = '';
            heroVisual?.classList.remove('is-fixed');
            if (heroVisual) {
                heroVisual.style.left = '';
                heroVisual.style.width = '';
            }
            mask?.style.removeProperty('--mask-white-opacity');
        };
    }, [isEditStatus, title, desc, product_title, list, more_btn_text, more_btn_href]);

    return (
        <GloLayout
            className={classNames('editor-index-large-card-jp', { 'is-edit-status': isEditStatus })}
            id={id}
            data={data}
            floorName="index-large-card-jp"
        >
            {/* 滚动容器：高度由 JS 设置为 100vh + maxTranslate */}
            <div className="index-large-card-jp-scroll-section" ref={scrollSectionRef}>
                {/* sticky 视口层：背景/遮罩不动，内部 content 由 JS 上移 */}
                <div className="index-large-card-jp-hero" ref={heroRef}>
                    <div className="index-large-card-jp-hero-visual" ref={heroVisualRef}>
                        {(bg_pc || bg_h5) && (
                            <EcoImg
                                pc={toEcoImgBackgroundSrc(bg_pc)}
                                pad={toEcoImgBackgroundSrc(bg_pc)}
                                h5={toEcoImgBackgroundSrc(bg_h5)}
                                className="index-large-card-jp-hero-bg"
                            />
                        )}
                        <div className="index-large-card-jp-hero-mask" ref={maskRef} />
                    </div>
                    {/* translateY 作用层：包含文案区 + 产品区 */}
                    <div className="index-large-card-jp-hero-scroll-content" ref={contentRef}>
                        <div className="index-large-card-jp-hero-content">
                            {!!title && <div className="index-large-card-jp-hero-title">{title}</div>}
                            {!!desc && <p className="index-large-card-jp-hero-desc">{desc}</p>}
                            {hasHeroBtn && (
                                <EcoButton
                                    className="index-large-card-jp-hero-btn"
                                    lTheme="black"
                                    lType="filled"
                                    lRadius="ellipse"
                                    lHref={more_btn_href}
                                    lChildren={more_btn_text}
                                // lDataLayer={{
                                //     ...getUrlGtm(more_btn_href, locale, {
                                //         list_name: title,
                                //         button_name: more_btn_text,
                                //         product_name: title,
                                //     }),
                                //     __btn_id1: `editor-${id} .index-large-card-jp-hero-btn`,
                                //     __btn_id2: more_btn_href,
                                //     __btn_type: 'href',
                                // }}
                                />
                            )}
                        </div>
                        {/* ref 用于计算 maxTranslate；is-title-two-lines 时 SCSS 缩减 padding-top */}
                        <div className="index-large-card-jp-products" ref={productsRef}>
                            {!!product_title && <div className="index-large-card-jp-products-title eco-content-v2">{product_title}</div>}

                            <Swiper
                                className="index-large-card-jp-products-swiper"
                                modules={[Navigation]}
                                navigation={{ prevEl: `#editor-${id} .dkzj-swiper-Left`, nextEl: `#editor-${id} .dkzj-swiper-Right` }}
                                spaceBetween={12}
                                slidesPerView="auto"
                                initialSlide={initIndex}
                            >
                                {list.map((item: any, idx: number) => {
                                    // const lGtm = {
                                    //     ...getUrlGtm(item.lHref, locale, {
                                    //         list_name: product_title || title,
                                    //         product_name: item.title,
                                    //         button_name: item.lBtnContent,
                                    //     }),
                                    //     position: `Homepage_ProductList_1_leftBtn`,
                                    //     __btn_id1: `editor-${id} .dkzj-item-l-${idx}`,
                                    //     __btn_id2: item.lHref,
                                    //     __btn_type: 'href',
                                    // };
                                    // const rGtm = {
                                    //     ...getUrlGtm(item.rHref, locale, {
                                    //         list_name: product_title || title,
                                    //         product_name: item.title,
                                    //         button_name: item.rBtnContent,
                                    //     }),
                                    //     position: `Homepage_ProductList_1_rightBtn`,
                                    //     __btn_id1: `editor-${id} .dkzj-item-r-${idx}`,
                                    //     __btn_id2: item.rHref,
                                    //     __btn_type: 'href',
                                    // };

                                    const itemImg = toEcoImgBackgroundSrc(item.img || item.pc || item.h5);

                                    return (
                                        <SwiperSlide className="index-large-card-jp-products-slide" key={idx}>
                                            <div className={`index-large-card-jp-item dkzj-item-${idx}`}>
                                                <div className="index-large-card-jp-item-img-wrap">
                                                    <EcoImg pc={itemImg} pad={itemImg} h5={itemImg} className="index-large-card-jp-item-img" />
                                                    {!!item.tag && <div className="index-large-card-jp-item-tag">{item.tag}</div>}
                                                </div>
                                                <div className="index-large-card-jp-item-msg">
                                                    {!!item.title && <div className="index-large-card-jp-item-title">{item.title}</div>}
                                                    {!!item.desc && <ItemDesc text={item.desc} />}
                                                    <div className="index-large-card-jp-item-btns">
                                                        {!!item.lBtnContent && (
                                                            <EcoButton
                                                                className={`dkzj-item-l-${idx}`}
                                                                lTheme="black"
                                                                lType="filled"
                                                                lRadius="ellipse"
                                                                lHref={item.lHref}
                                                                lChildren={item.lBtnContent}
                                                            // lDataLayer={lGtm}
                                                            />
                                                        )}
                                                        {!!item.rBtnContent && (
                                                            <EcoButton
                                                                className={`dkzj-item-r-${idx}`}
                                                                lTheme="black"
                                                                lType="outlined"
                                                                lRadius="ellipse"
                                                                lHref={item.rHref}
                                                                lChildren={item.rBtnContent}
                                                            // lDataLayer={rGtm}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    );
                                })}
                                <div className="index-large-card-jp-control">
                                    <div
                                        className="swiper-button-prev GtmModuleData-btn-common dkzj-swiper-Left"
                                        data-btn-id="dkzj-swiper-Left"
                                    ></div>
                                    <div
                                        className="swiper-button-next GtmModuleData-btn-common dkzj-swiper-Right"
                                        data-btn-id="dkzj-swiper-Right"
                                    ></div>
                                </div>
                            </Swiper>
                        </div>
                    </div>
                </div>
            </div>
        </GloLayout>
    );
}
